import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Returns a string representing the time elapsed since the given date.
 * @param {Date} date - The date to calculate the time elapsed from.
 * @returns {string} A string representing the time elapsed since the given date.
 * @example useTimeSince(new Date('2021-01-01T00:00:00.000Z'))
 */
export const useTimeSince = (date: Date): string => {
  const { t } = useTranslation()

  const timeSince = useMemo(() => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

    const intervals = {
      year: 31536000,
      month: 2592000,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1,
    }

    let counter = 0

    for (const [key, value] of Object.entries(intervals)) {
      counter = Math.floor(seconds / value)

      if (counter > 0) {
        if (counter === 1) {
          const term = t(`date.${key}`, { defaultValue: key })

          return t('general.time_since', {
            count: counter,
            term,
            defaultValue: `${counter} ${term} ago`,
          })
        } else {
          const term = t(`date.${key}_other`, { defaultValue: key })

          return t('general.time_since', {
            count: counter,
            term,
            defaultValue: `${counter} ${term}s ago`,
          })
        }
      }
    }

    return 'Just now'
  }, [date])

  return timeSince
}
